import React from 'react';
import { FileTextIcon, Clock3Icon, BarChart3Icon, TrendingUpIcon, ArrowUpIcon, InfoIcon } from 'lucide-react';
import { results } from '../../../data/richelieuProof';
type P = {
  isFR: boolean;
};
function KpiShell({
  icon: Icon,
  children
}: {
  icon: typeof FileTextIcon;
  children: React.ReactNode;
}) {
  return <div className="rounded-xl border border-[#E8E8F0] bg-white p-4 flex items-start gap-3.5 h-full" data-id="element-6021" data-mp="2l63tt6fmjbyo">
      <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-6022" data-mp="3is18esqvux5c">
        <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-6023" data-mp="3duhw3ydqwug1" />
      </span>
      <div className="min-w-0" data-id="element-6024" data-mp="391ygkvvhzp8l">{children}</div>
    </div>;
}
function SlaChart({
  isFR
}: P) {
  const pick = (v: {
    fr: string;
    en: string;
  }) => isFR ? v.fr : v.en;
  const pts = results.chart.points;
  // y-axis 40 → 80
  const W = 280;
  const H = 64;
  const toY = (v: number) => H - (v - 40) / 40 * H;
  const xs = [18, 140, 262];
  return <div className="rounded-xl border border-[#E8E8F0] bg-white p-4 h-full" data-id="element-6025" data-mp="3f4k71do3tf2x">
      <p className="flex items-center gap-2 font-sans text-[13px] font-semibold text-[#121212]" data-id="element-6026" data-mp="3f6okonbt1sge">
        <TrendingUpIcon className="w-4 h-4 text-[#4F46E5]" aria-hidden="true" data-id="element-6027" data-mp="3j89t9whb3kks" />
        {pick(results.chart.title)}
      </p>
      <div className="mt-2 grid grid-cols-[30px_minmax(0,1fr)] gap-1.5" data-id="element-6028" data-mp="3advdztws0a1o">
        <div className="flex flex-col justify-between font-sans text-[9.5px] text-[#8A90A0] tabular-nums h-[64px]" data-id="element-6029" data-mp="3abr1wooq44qb">
          {['80', '60', '40'].map(v => <span key={v} data-id="element-6030" data-mp="30gqpvojde4i8">{isFR ? `${v} %` : `${v}%`}</span>)}
        </div>
        <div data-id="element-6031" data-mp="2mgoeml5ki9by">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[64px] overflow-visible" role="img" aria-label="53 → 58 → 60" data-id="element-6032" data-mp="2mgy324ays8fj">
            {[0, H / 2, H].map(y => <line key={y} x1="0" x2={W} y1={y} y2={y} stroke="#EEEEF4" strokeWidth="1" data-id="element-6033" data-mp="kot7miu9at02" />)}
            <polyline points={pts.map((p, i) => `${xs[i]},${toY(p.value)}`).join(' ')} fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6034" data-mp="po647vaxjefu" />
            {pts.map((p, i) => <g key={p.value} data-id="element-6035" data-mp="e7hxfdp0wruk" data-mpi={i}>
                <circle cx={xs[i]} cy={toY(p.value)} r="4" fill="#4F46E5" stroke="#fff" strokeWidth="1.5" data-id="element-6036" data-mp="e8t8lykst1vp" />
                <text x={xs[i]} y={toY(p.value) - 9} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#4F46E5" fontFamily="Inter, sans-serif" data-id="element-6037" data-mp="9b17cdy9k3eu">
                  {isFR ? `${p.value} %` : `${p.value}%`}
                </text>
              </g>)}
          </svg>
          <div className="mt-1.5 flex justify-between font-sans text-[9.5px] text-[#6B7180]" data-id="element-6038" data-mp="x5ufjoxwxc2a">
            {pts.map(p => <span key={p.value} data-id="element-6039" data-mp="s5yd3u4v5sou">{pick(p.label)}</span>)}
          </div>
        </div>
      </div>
    </div>;
}
export function ResultsPanel({
  isFR
}: P) {
  const pick = (v: {
    fr: string;
    en: string;
  }) => isFR ? v.fr : v.en;
  const r = results;
  return <div className="rounded-2xl border border-[#E8E8F0] bg-white p-5 md:p-7 shadow-[0_18px_40px_-34px_rgba(40,36,120,0.35)]" data-id="element-6040" data-mp="1s964cine9dwl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2" data-id="element-6041" data-mp="1x922405i093p">
        <div data-id="element-6042" data-mp="224r3m912w02n">
          <p className="font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-6043" data-mp="228eypcm68ujh">{pick(r.eyebrow)}</p>
          <h3 className="font-serif text-[1.7rem] md:text-[2rem] text-[#121212] leading-tight mt-1.5" data-id="element-6044" data-mp="2b709umpbd7a8">{pick(r.title)}</h3>
        </div>
        <p className="font-sans text-[12px] text-[#6B7180] md:pb-2" data-id="element-6045" data-mp="2g4inuri6ucp3">{pick(r.note)}</p>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1.35fr] gap-3" data-id="element-6046" data-mp="2kxv0e39let1h">
        <KpiShell icon={FileTextIcon} data-id="element-6047" data-mp="2pubowykpf9f3">
          <p className="font-serif text-[1.75rem] text-[#121212] leading-none whitespace-nowrap" data-id="element-6048" data-mp="2pxhc289rw186">{pick(r.reentry.value)}</p>
          <p className="font-serif text-[14px] text-[#121212] mt-2" data-id="element-6049" data-mp="2yvtc10wj4bda">{pick(r.reentry.label)}</p>
          <p className="font-sans text-[12px] text-[#5B6478] mt-0.5" data-id="element-6050" data-mp="fjxckgksq47">{pick(r.reentry.sub)}</p>
          <p className="font-sans text-[10.5px] text-[#8A90A0] leading-snug mt-2.5" data-id="element-6051" data-mp="3rmcv1ec5u73z">{pick(r.reentry.note)}</p>
        </KpiShell>
        <KpiShell icon={Clock3Icon} data-id="element-6052" data-mp="972aemi86pt2">
          <p className="font-serif text-[1.75rem] text-[#121212] leading-none whitespace-nowrap" data-id="element-6053" data-mp="9a80o52lbxqd">{pick(r.time.value)}</p>
          <p className="font-serif text-[14px] text-[#121212] mt-2" data-id="element-6054" data-mp="3dmbw44fgh9r5">{pick(r.time.label)}</p>
          <p className="font-sans text-[12px] text-[#5B6478] mt-0.5" data-id="element-6055" data-mp="3do6gnhmtezup">{pick(r.time.sub)}</p>
        </KpiShell>
        <KpiShell icon={BarChart3Icon} data-id="element-6056" data-mp="3mn0bb2gl5g5m">
          <p className="font-serif text-[1.75rem] text-[#121212] leading-none whitespace-nowrap" data-id="element-6057" data-mp="3mo2x4vz1wseo">{pick(r.sla.value)}</p>
          <p className="font-serif text-[14px] text-[#121212] mt-2" data-id="element-6058" data-mp="3hmdplqb1ewvc">{pick(r.sla.label)}</p>
          <p className="font-sans text-[12px] text-[#5B6478] mt-0.5" data-id="element-6059" data-mp="11v7wbcl0mkuh">{pick(r.sla.sub)}</p>
          <p className="flex items-center gap-1 font-sans text-[11px] text-[#1E8A45] mt-2.5" data-id="element-6060" data-mp="wwwm30qt68bq">
            <ArrowUpIcon className="w-3 h-3" aria-hidden="true" data-id="element-6061" data-mp="15dng880fkwoj" />
            {pick(r.sla.trend)}
          </p>
        </KpiShell>
        <div className="sm:col-span-2 xl:col-span-1" data-id="element-6062" data-mp="19hmx4rpmhxif">
          <SlaChart isFR={isFR} data-id="element-6063" data-mp="vhc4bj4r68um" />
        </div>
      </div>

      <p className="mt-5 flex items-start gap-2.5 font-sans text-[11.5px] text-[#6B7180] leading-relaxed" data-id="element-6064" data-mp="vivjt7il4fvc">
        <InfoIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-px" aria-hidden="true" data-id="element-6065" data-mp="1o57x4cr7bgm4" />
        {pick(r.footnote)}
      </p>
    </div>;
}